// The page sources this browser has drawn (cloud ADR 0393, TOM-665; contract
// §3.4). A board read sends a version 2 page's html and css empty when the
// reader says it holds them (`known`), so a board of forty template pages is
// not sent again in full every minute. This cache keeps what was drawn, by the
// server's hash of the stored html and css: at most 200 sources, the least
// recently used out. The board sends the hashes it holds for its own pages on
// every re-read, and reads again without them when a source it left out is
// not here any more.

/** The server's hash of a page's html and css: 22 base64url characters. */
export const PAGE_SOURCE_HASH = /^[A-Za-z0-9_-]{22}$/;

/** At most this many sources are kept. */
export const PAGE_SOURCE_CACHE_MAX = 200;

/** At most this many hashes go in one read's `known` (the route ignores the rest). */
export const PAGE_KNOWN_MAX = 64;

/** A page's own source: the stored html and css. */
export interface PageSource {
  html: string;
  css: string;
}

export class PageSourceCache {
  private readonly entries = new Map<string, PageSource>();

  constructor(readonly max: number = PAGE_SOURCE_CACHE_MAX) {}

  /** The source held under `hash`, now the most recently used; null when none. */
  get(hash: string): PageSource | null {
    const entry = this.entries.get(hash);
    if (entry === undefined) return null;
    this.entries.delete(hash);
    this.entries.set(hash, entry);
    return entry;
  }

  /** Keep a source under its hash; the least recently used goes past the cap. */
  put(hash: string, source: PageSource): void {
    if (!PAGE_SOURCE_HASH.test(hash)) return;
    this.entries.delete(hash);
    this.entries.set(hash, { html: source.html, css: source.css });
    while (this.entries.size > this.max) {
      const oldest = this.entries.keys().next().value;
      if (oldest === undefined) break;
      this.entries.delete(oldest);
    }
  }

  has(hash: string): boolean {
    return this.entries.has(hash);
  }

  get size(): number {
    return this.entries.size;
  }

  /** Of `hashes`, the ones held, each once, at most PAGE_KNOWN_MAX: a read's `known`. */
  known(hashes: Iterable<string>): string[] {
    const out = new Set<string>();
    for (const hash of hashes) {
      if (out.size >= PAGE_KNOWN_MAX) break;
      if (PAGE_SOURCE_HASH.test(hash) && this.entries.has(hash)) out.add(hash);
    }
    return [...out];
  }

  clear(): void {
    this.entries.clear();
  }
}

/** This tab's cache, shared by every board it reads. */
export const pageSources = new PageSourceCache();

/** A board read's URL with the hashes it holds, or unchanged when it holds none. */
export function withKnownSources(url: string, known: readonly string[]): string {
  if (known.length === 0) return url;
  return `${url}${url.includes('?') ? '&' : '?'}known=${known.join(',')}`;
}
